import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../rtk/_config/hooks";
import { RootState } from "../../../types/rtk/api-config/types";
import { useGetOffersQuery } from "../../../rtk/offers/offers";
import { useGetTrackerStateQuery } from "../../../rtk/tracker/tracker";
import { AppRoutes } from "../../../routes/AppRoutes";
import SpinnerLoader from "../../atoms/SpinnerLoader/SpinnerLoader";

const OrderTemplate = () => {
  const navigate = useNavigate();
  const [initializing, setInitializing] = useState<boolean>(true);
  const external_id = useAppSelector((state: RootState) => state.auth.external_id);
  const token = useAppSelector((state: RootState) => state.auth.token);
  const delegation_token = useAppSelector((state: RootState) => state.auth.delegation_token);
  // Authenticated in either mode: legacy external_id, an apiv5 access token, or a
  // delegation token (the access token is derived from it on demand by the API
  // layer — it may not be set yet on the first render while the exchange is still
  // in flight; bouncing to /unknown then would be a false negative).
  const isAuthenticated = Boolean(external_id || token || delegation_token);
  const tracker_id = useAppSelector((state: RootState) => state.tracker.tracker_id);
  const active_subscription = useAppSelector((state: RootState) => state.tracker.active_subscription);
  const { data: offersData, isFetching: offersFetching, isError: offersError } = useGetOffersQuery();
  const { data: trackerData, isFetching: trackerFetching, isError: trackerError } = useGetTrackerStateQuery();

  useEffect(() => {
    (async () => {
      // Redirect if no auth
      if (!isAuthenticated) {
        navigate(`${AppRoutes.UNKNOWN}${window.location.search}`);
        return;
      }

      // Wait until both requests have settled before judging success/failure.
      // The apiv5 access token may still be exchanging on the very first render,
      // and a transient 401 triggers a re-exchange + retry under the hood; acting
      // on an in-flight error here would wrongly bounce to /unknown.
      if (offersFetching || trackerFetching) {
        return;
      }

      if (trackerError || offersError) {
        console.log("Error fetching data:", { trackerError, offersError, trackerData, offersData });
        navigate(`${AppRoutes.UNKNOWN}${window.location.search}`);
        return;
      }

      if (trackerData && trackerData.active_subscription) {
        navigate(`${AppRoutes.MANAGE_SUBSCRIPTIONS}${window.location.search}`);
        return;
      }

      setInitializing(false);
    })();
  }, [isAuthenticated, tracker_id, active_subscription, navigate, offersData, offersFetching, offersError, trackerData, trackerFetching, trackerError]);

  if (initializing) {
    return <SpinnerLoader size={100} />;
  }
  return <Outlet />;
};

export default OrderTemplate;